import cx from 'clsx';
import * as React from 'react';

import Asset from '@/components/AssetLayer/Asset';
import LottiePlayer from '@/components/LottiePlayer';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { useWindowBreakpoint } from '@/hooks/useWindowBreakpoint';
import { IconType } from '@/types/common';

import { ANIMATION_DEFAULT_DELAY, ANIMATION_INITIAL_DELAY } from './constants';
import styles from './Lottie.module.scss';
import {
  fetchJsonAnimation,
  fetchLottieAnimation,
  pickAssetForCurrentBreakpoint,
} from './utils';

export interface LottieAnimationProps {
  assets?: {
    small: IconType;
    medium: IconType;
    large: IconType;
  };
  asset?: IconType;
  delay?: number;
  ratio?: number;
  autoplay?: boolean;
  initialDelay?: number;
  loop?: boolean;
  forcePlay?: boolean;
  onAnimationEnd?: () => void;
}

const LottieAnimation: React.FC<LottieAnimationProps> = ({
  assets,
  asset,
  autoplay = true,
  delay = ANIMATION_DEFAULT_DELAY,
  initialDelay = ANIMATION_INITIAL_DELAY,
  ratio = 0,
  loop = true,
  forcePlay,
  onAnimationEnd,
}) => {
  const [lottieImage, setLottieImage] = React.useState<null | string>(null);
  const [normalImage, setNormalImage] = React.useState<null | string>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const { breakpoint } = useWindowBreakpoint();
  const { ref, intersected } = useIntersectionObserver(false);

  React.useEffect(() => {
    let assetToFetch = null;
    if (assets)
      assetToFetch = pickAssetForCurrentBreakpoint(breakpoint, assets);
    else if (asset) assetToFetch = asset;
    let timout: NodeJS.Timeout;

    const setAndPlayJSONAnimation = (data: string | null) => {
      if (!data) {
        setLottieImage(null);
        setNormalImage(null);
        return;
      }

      setLottieImage(data);
      setNormalImage(null);
      if (forcePlay) setIsPlaying(true);
      if (autoplay) {
        timout = setTimeout(() => {
          setIsPlaying(true);
        }, initialDelay);
      }
    };

    if (assetToFetch && assetToFetch.subtype === 'json') {
      fetchJsonAnimation(assetToFetch.source).then(animationData =>
        setAndPlayJSONAnimation(animationData)
      );
    } else if (assetToFetch && assetToFetch.subtype === 'octet-stream') {
      fetchLottieAnimation(assetToFetch.source).then(animationData =>
        setAndPlayJSONAnimation(animationData)
      );
    } else if (assetToFetch) {
      setNormalImage(assetToFetch.source);
      setLottieImage(null);
    }

    return () => clearTimeout(timout);
  }, [
    breakpoint,
    assets,
    asset,
    autoplay,
    initialDelay,
    forcePlay,
    onAnimationEnd,
  ]);

  const prepareAnimationComponent = () => {
    if (normalImage) {
      return (
        <Asset
          intersected={!!intersected}
          imageSrc={{ normal: normalImage }}
          type="image"
        />
      );
    }

    if (lottieImage) {
      const handleAnimationLoopComplete = () => {
        if (!delay) return;
        setIsPlaying(false);
        const timeout = setTimeout(() => {
          setIsPlaying(true);
        }, delay);
        return () => clearTimeout(timeout);
      };

      const handleAnimationComplete = () => {
        if (onAnimationEnd) {
          setIsPlaying(false);
          onAnimationEnd();
        }
      };

      return (
        <LottiePlayer
          animationData={lottieImage}
          autoplay={false}
          loop={loop}
          onLoopComplete={handleAnimationLoopComplete}
          onComplete={handleAnimationComplete}
          paused={intersected ? !isPlaying : true}
          style={{
            width: 'inherit',
            height: 'inherit',
          }}
        />
      );
    }

    return null;
  };

  const animationCmp = prepareAnimationComponent();
  return (
    <div
      ref={ref}
      data-testid="lottie-animation-wrapper"
      className={cx(
        styles.animationWrapper,
        !animationCmp && styles[`animationWrapperBefore__${ratio}`]
      )}
    >
      {animationCmp}
    </div>
  );
};

export default LottieAnimation;
